<template>
  <div>
    <exo-form hideClose :root="_self" :clear="false">
      <template v-slot:titleTooltip>
        <exo-help-dialog
          :color="'black--text'"
          :margin="'m-0'"
          title="Constellation Simulation"
        >
          <tooltipNumericalConstellationSimulationMission />
        </exo-help-dialog>
      </template>
      <exo-input-name :root="_self" />
      <v-row>
        <v-col cols="6">
          <exo-card-form label="Simulation">
            <exo-input-ref-notparametric
              :root="_self"
              name="spacecraft"
              type="spacecraft"
              componentRoute="/numericalspacecraft"
            />
            <exo-input-ref-notparametric
              :root="_self"
              name="constellation"
              type="constellation"
              componentRoute="/analyticalconstellationheterogeneous"
            >
              <exo-help-dialog title="Constellation">
                <tooltipConstellationGeometry />
              </exo-help-dialog>
            </exo-input-ref-notparametric>
            <exo-form-alert class="mb-2" type="warning" v-if="missingPayload">
              A payload has to be defined in the constellation.
            </exo-form-alert>

            <exo-input-string :root="_self" name="firstTLELine" />
            <exo-input-string :root="_self" name="secondTLELine" />

            <exo-input-ref-multiple
              :root="_self"
              name="groundStations"
              :isParametric="false"
              type="groundStation"
              componentRoute="/analyticalconstellationgroundstation"
            />
            <exo-input-ref-multiple
              :root="_self"
              name="isolatedPointsOfInterest"
              :isParametric="false"
              type="isolatedPointsOfInterest"
              componentRoute="/analyticalconstellationearthpointofinterest"
            />
            <exo-input-double :root="_self" name="simulationDuration">
              <exo-help-dialog title="Simulation duration">
                For phased orbits, the simulation duration can be equal to a
                multiple of the repetition period. In other cases, be sure to
                select long-enough a simulation duration to ensure meaningful
                outputs.
              </exo-help-dialog>
            </exo-input-double>

            <exo-input-double :root="_self" name="ephemeridesStepInSeconds">
              <exo-help-dialog title="Simulation time step">
                The simulation time step is the time step used to compute
                ephemerides datas.
              </exo-help-dialog>
            </exo-input-double>
            <exo-input-date-time :root="_self" name="simulationDate">
              <exo-help-dialog margin="mt-1 ml-2" title="Simulation date">
                The simulation date is used as reference for the definition of
                the RAAN / MLTAN of each planes of the constellation. It is used
                also to compute the Sun position if the sunlight is considered.
              </exo-help-dialog>
            </exo-input-date-time>
          </exo-card-form>
        </v-col>
        <v-col cols="6">
          <exo-card-form label="Options">
            <exo-input-select
              :multiple="true"
              :root="_self"
              name="perturbations"
            >
              <exo-help-dialog title="Perturbations">
                <tooltipPerturbations />
              </exo-help-dialog>
            </exo-input-select>
            <exo-input-double
              :root="_self"
              v-if="
                inputs.perturbations != null &&
                inputs.perturbations.includes('DRAG')
              "
              name="customSolarFlux"
            >
              <exo-help-dialog title="Solar flux">
                <tooltipSolarFlux />
              </exo-help-dialog>
            </exo-input-double>

            <exo-input-double :root="_self" name="minStationVisibilityTime">
              <exo-help-dialog title="Minimum station visibility duration">
                <tooltipConstellationMinimumStationVisibilityDuration />
              </exo-help-dialog>
            </exo-input-double>
            <exo-input-select :root="_self" name="telecomChoice">
              <exo-help-dialog title="Link budget type">
                You can simulate the evolution of data storage by choosing one
                of the data gathering type:
                <ul>
                  <li>
                    <b>Continuous:</b> the amount of data generated depends on
                    the visibility events duration
                  </li>
                  <li>
                    <b>Discontinuous:</b> the amount of data generated depends
                    only on the number of visibility events
                  </li>
                </ul>
              </exo-help-dialog>
            </exo-input-select>
            <exo-input-double :root="_self" name="dataSize" />
            <exo-input-double :root="_self" name="dataRate" />

            <!-- <exo-input-ref-notparametric :root="_self" name="earthMesh" type="earthMesh" componentRoute="/analyticalconstellationearthmesh" />
            -->
          </exo-card-form>
        </v-col>
      </v-row>
    </exo-form>
  </div>
</template>

<script>
import numericalForms from '../../mixins/numericalForms.js'
import forms from '../../mixins/forms.js'
import dataTools from '../../tools/data.js'
import missionTools from '../../store/mission.js'
import fieldsDescription from '../../tools/fieldsDescription.json'
import { mapGetters, mapActions } from 'vuex'
import tooltipNumericalConstellationSimulationMission from '../../components/Tooltips/ConstellationModule/tooltipNumericalConstellationSimulationMission'
import tooltipPerturbations from '../../components/Tooltips/tooltipPerturbations'
import tooltipSolarFlux from '../../components/Tooltips/ConstellationModule/tooltipNumericalConstellationSolarFlux'
import tooltipConstellationGeometry from '../../components/Tooltips/ConstellationModule/tooltipConstellationGeometry'
import tooltipConstellationMinimumStationVisibilityDuration from '../../components/Tooltips/ConstellationModule/tooltipConstellationMinimumStationVisibilityDuration'
import Vue from 'vue'

export default {
  name: 'NumericalConstellationMission',

  mixins: [forms, numericalForms],

  components: {
    tooltipNumericalConstellationSimulationMission,
    tooltipPerturbations,
    tooltipSolarFlux,
    tooltipConstellationGeometry,
    tooltipConstellationMinimumStationVisibilityDuration
  },

  async mounted() {
    this.$root.$on(
      'request-computation-constellation-simulation',
      this.requestComputationConstellationSimulationHandler
    )
    this.$root.$on('copy-mission', this.copyMissionHandler)
    this.$root.$on('create-mission', this.createMissionHandler)
    this.$root.$on('current-mission-updated', this.currentMissionUpdatedHandler)
    await this.refreshReferences()
    this.refreshForm()
    await this.refreshGroundStations()
    await this.refreshIsolatedPointsOfInterest()
  },

  beforeDestroy() {
    this.$root.$off(
      'request-computation-constellation-simulation',
      this.requestComputationConstellationSimulationHandler
    )
    this.$root.$off('copy-mission', this.copyMissionHandler)
    this.$root.$off('create-mission', this.createMissionHandler)
    this.$root.$off(
      'current-mission-updated',
      this.currentMissionUpdatedHandler
    )
  },

  computed: {
    ...mapGetters(['currentMission']),

    missingPayload() {
      if (this.inputs.constellation) {
        if (this.inputs.constellation.homogeneousConstellationGeometries[0]) {
          if (
            !this.inputs.constellation.homogeneousConstellationGeometries[0]
              .payload
          ) {
            return true
          }
        }
      }
      return false
    },

    inputsValid() {
      let fieldsState = this.verifyPropertiesList(
        this.getRequiredPropertiesList(this.inputs),
        this.inputs,
        true
      )
      fieldsState =
        fieldsState &&
        this.verifyPropertiesList(
          this.getOptionalPropertiesList(this.inputs),
          this.inputs,
          false
        )
      const groundStationsSet =
        this.inputs.groundStations != null &&
        this.inputs.groundStations.length > 0
      return (
        this.inputNameState &&
        fieldsState &&
        groundStationsSet &&
        !this.missingPayload
      )
    }
  },

  data() {
    return {
      tableFields: [
        { key: 'spacecraft', label: 'Spacecraft', sortable: true },
        { key: 'groundStations', label: 'GroundStations', sortbale: true },
        {
          key: 'isolatedPointsOfInterest',
          label: 'Earth Isolated Points of Interest',
          sortable: true
        }
      ],

      enumPropertiesList: this.getEnumProperties(),

      modalRef: { content: [] },
      showModal: false,

      groundStationsNotParametrics: [],
      spacecraftsNotParametrics: [],
      constellationsNotParametrics: [],
      isolatedPointsOfInterestsNotParametrics: [],

      inputs: {
        name: this.getDefaultValue('name'),
        // firstTLELine: this.getDefaultValue('firstTLELine'),
        // secondTLELine: this.getDefaultValue('secondTLELine'),
        description: this.getDefaultValue('description'),
        constellation: null,
        groundStations: [],
        spacecraft: null,
        simulationDuration: this.getDefaultValue('simulationDuration'),
        ephemeridesStepInSeconds: this.getDefaultValue(
          'ephemeridesStepInSeconds'
        ),
        minStationVisibilityTime: this.getDefaultValue(
          'minStationVisibilityTime'
        ),
        simulationDate: this.getDefaultValue('simulationDate'),
        isolatedPointsOfInterest: null,
        perturbations: [],
        dataSize: this.getDefaultValue('dataSize'),
        dataFlow: this.getDefaultValue('dataFlow'),
        telecomChoice: this.getDefaultValue('telecomChoice')
      }
    }
  },

  methods: {
    ...mapActions(['setCurrentMission']),

    requestComputationConstellationSimulationHandler() {
      this.onSave()
    },
    copyMissionHandler(mission) {
      this.copyMission(mission)
    },
    createMissionHandler(mission) {
      this.createMission(mission)
    },
    currentMissionUpdatedHandler() {
      this.refreshForm()
    },

    refreshTable() {},

    updateMissionFields(fields) {
      if (fields.typeName === this.getCurrentFormType()) {
        for (let field in fields) {
          this.inputs[field] = fields[field]
        }
      }
    },

    async refreshReferences() {
      await this.refreshSpacecrafts()
      await this.refreshConstellations()
      await this.refreshGroundStations()
      await this.refreshIsolatedPointsOfInterest()
    },

    copyMission(mission) {
      if (mission && mission.typeName === this.getCurrentFormType()) {
        this.copy(mission)
        this.sendFormToServer()
      }
    },

    async refreshIsolatedPointsOfInterest() {
      let self = this
      await this.$http
        .get(this.getAPI(`pointofinterestdefinitions`))
        .then((response) => {
          self.isolatedPointsOfInterestsNotParametrics = response.data
            .map((obj) => ({ value: obj, text: obj.displayedName }))
            .sort((a, b) =>
              a.text.toLowerCase() < b.text.toLowerCase() ? -1 : 1
            )
        })
        .catch((e) => {
          self.displayMessage(
            'Error getting list of isolated points of interest. ',
            e,
            'notification',
            'error'
          )
        })
    },

    async refreshConstellations() {
      let self = this
      await this.$http
        .get(this.getAPI(`constellationdefinitions`))
        .then((response) => {
          self.constellationsNotParametrics = response.data
            .map((obj) => ({ value: obj, text: obj.displayedName }))
            .sort((a, b) =>
              a.text.toLowerCase() < b.text.toLowerCase() ? -1 : 1
            )
        })
        .catch((e) => {
          self.displayMessage(
            'Error getting list of constellations. ',
            e,
            'notification',
            'error'
          )
        })
    },

    refreshForm() {
      if (
        this.currentMission != null &&
        this.currentMission.typeName === this.getCurrentFormType()
      ) {
        if (this.currentMission.id !== this.inputs.id) {
          // different mission reload completely the form
          this.edit(this.currentMission, 0)
        } else {
          // reloading the current mission
          // only refresh the updated fields
          const fieldsToUpdate = [
            this.groundStationsNotParametrics,
            this.spacecraftsNotParametrics,
            this.constellationsNotParametrics,
            this.isolatedPointsOfInterestsNotParametrics
          ]
          missionTools.updateAllReferences(this, fieldsToUpdate)
        }
      }
    },

    async refreshSpacecrafts() {
      let self = this
      await this.$http
        .get(this.getAPI('spacecraftdefinitions'))
        .then((response) => {
          self.spacecraftsNotParametrics = response.data
            .filter((obj) => !obj.displayedName.endsWith('*'))
            .map((obj) => ({ value: obj, text: obj.displayedName }))
            .sort((a, b) =>
              a.text.toLowerCase() < b.text.toLowerCase() ? -1 : 1
            )
        })
        .catch((e) => {
          self.displayMessage(
            'Error getting list of spacecrafts. ',
            e,
            'NumericalMissionErrorDisplay',
            'error'
          )
        })
    },

    getRequiredPropertiesList(instance) {
      let subTypes = this.getSubTypes(instance)
      let objectType = this.getCurrentFormType()
      let requiredList = dataTools.getPropertiesList(
        fieldsDescription[objectType],
        subTypes,
        'required'
      )
      return requiredList
    },

    getOptionalPropertiesList(instance) {
      let subTypes = this.getSubTypes(instance)
      let objectType = this.getCurrentFormType()
      return dataTools.getPropertiesList(
        fieldsDescription[objectType],
        subTypes,
        'optional'
      )
    },

    async refreshGroundStations() {
      // TODO MFI automatic method for all of these getters
      let self = this
      await this.$http
        .get(this.getAPI(`groundstationdefinitions`))
        .then((response) => {
          self.groundStationsNotParametrics = response.data
            .map((obj) => ({ value: obj, text: obj.displayedName }))
            .sort((a, b) =>
              a.text.toLowerCase() < b.text.toLowerCase() ? -1 : 1
            )
        })
        .catch((e) => {
          self.displayMessage(
            'Error getting list of groundStations. ',
            e,
            'notification',
            'error'
          )
        })
    },

    onSave: function (evt) {
      this.inputs.saving = true
      this.$http
        .put(
          this.getCurrentAPI() + '/' + this.inputs.id,
          this.createObjectFromForm()
        )
        .then((response) => {
          this.inputs.saving = false

          this.$root.$emit('update-mission-list')

          this.$root.$emit('set-current-mission', response.data) // update fields and current mission
          // redirect to dashboard

          this.$router.push({ path: '/numericalconstellationdashboard' })
        })
        .catch((e) => {
          this.inputs.saving = false
          this.displayMessage(
            'Error creating ' + this.inputs.type + '. ',
            e,
            'notification',
            'error'
          )
        })
    },

    getRefList(param) {
      if (param === 'constellations') {
        return this.constellationsNotParametrics
      }
      if (param === 'groundStations') {
        return this.groundStationsNotParametrics
      }
      if (param === 'isolatedPointsOfInterest') {
        return this.isolatedPointsOfInterestsNotParametrics
      }
    },

    getCurrentFormTitle() {
      return 'Constellation Simulation'
    },

    getCurrentFormType() {
      return 'ConstellationSimulationRequest'
    },

    createMission(mission) {
      if (mission.typeName === this.getCurrentFormType()) {
        this.clearForm()
        this.updateMissionFields(mission)
        this.sendFormToServer()
      }
    },

    sendFormToServer() {
      this.inputs.saving = true
      this.$http
        .post(this.getCurrentAPI(), this.createObjectFromForm())
        .then((response) => {
          this.inputs.saving = false
          this.$root.$emit('set-current-mission', response.data)
        })
        .catch((e) => {
          this.inputs.saving = false
          this.displayMessage(
            'Error creating ' + this.inputs.type + '. ',
            e,
            'AnalyticalMissionErrorDisplay',
            'error'
          )
        })
    },

    addFieldsListToBaseObject(
      baseObject,
      fieldsList,
      parentObject = this.inputs
    ) {
      for (let field of fieldsList) {
        if (this.getPropertiesDescription()[field] != null) {
          let fieldType = this.getPropertiesDescription()[field].type
          if (
            fieldType === 'enum' ||
            fieldType === 'boolean' ||
            fieldType === 'date'
          ) {
            baseObject[field] = parentObject[field]
          } else if (fieldsDescription[fieldType] != null) {
            baseObject[field] = parentObject[field]
          } else {
            baseObject[field] = dataTools.convertGui2si(
              this.getFieldType(field),
              parentObject[field]
            )
          }
        }
      }
      return baseObject
    },

    getSubTypes(instance) {
      let types = {
        telecomChoice: instance.telecomChoice
      }
      return types
    },

    clearLocalFields() {
      for (let field in this.getPropertiesDescription()) {
        if (this.inputs.hasOwnProperty(field)) {
          this.inputs[field] = this.getDefaultValue(field)
        }
      }
    },

    addLocalFieldsFromForm(baseObject, fieldsList) {
      baseObject = this.addFieldsListToBaseObject(baseObject, fieldsList)
      baseObject.computationRequested = true
      return baseObject
    },
    isExoInputMultiple(item) {
      return item === 'groundStations' || item === 'isolatedPointsOfInterest'
    }
  }
}
</script>
